import React, { useEffect, useState } from 'react';
import { detectLanguage } from '../translations';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { getTranslation } from '../translations';
import { useToast } from '../context/ToastContext';

const AboutPage: React.FC = () => {
  const [lang, setLang] = useState('en');
  const [t, setT] = useState(getTranslation('en'));
  const { showToast } = useToast();

  useEffect(() => {
    const savedLang = localStorage.getItem('preferredLanguage');
    const detectedLang = savedLang || detectLanguage();
    setLang(detectedLang);
    setT(getTranslation(detectedLang));
  }, []);

  const handleLanguageChange = (newLang: string) => {
    setLang(newLang);
    setT(getTranslation(newLang));
    localStorage.setItem('preferredLanguage', newLang);
  };

  const isKorean = lang === 'ko';

  const teamMembers = [
    {
      name: isKorean ? '김성훈' : 'SeongHoon Kim',
      role: 'CEO & Founder',
      image: 'https://api.dicebear.com/7.x/avataaars/svg?seed=founder',
      bio: isKorean 
        ? '20년 이상의 기술 혁신 경험을 보유한 비전가'
        : 'Visionary with 20+ years of tech innovation experience'
    },
    {
      name: isKorean ? '이성훈' : 'SungHoon Lee',
      role: 'CTO',
      image: 'https://api.dicebear.com/7.x/avataaars/svg?seed=cto',
      bio: isKorean
        ? 'AI와 실시간 통신 기술의 전문가'
        : 'Expert in AI and real-time communication technologies'
    },
    {
      name: isKorean ? '데이비드 리' : 'David Lee',
      role: 'COO',
      image: 'https://api.dicebear.com/7.x/avataaars/svg?seed=product',
      bio: isKorean
        ? '글로벌 제품 전략 및 UX 디자인 리더'
        : 'Global product strategy and UX design leader'
    }
  ];

  const values = [
    {
      icon: '🌍',
      title: isKorean ? '글로벌 연결' : 'Global Connection',
      description: isKorean
        ? '언어 장벽을 넘어 전 세계 사람들을 연결합니다'
        : 'Connecting people worldwide beyond language barriers'
    },
    {
      icon: '🚀',
      title: isKorean ? '혁신' : 'Innovation',
      description: isKorean
        ? '최신 AI 기술로 커뮤니케이션의 미래를 만듭니다'
        : 'Creating the future of communication with cutting-edge AI'
    },
    {
      icon: '🤝',
      title: isKorean ? '신뢰' : 'Trust',
      description: isKorean
        ? '안전하고 신뢰할 수 있는 서비스를 제공합니다'
        : 'Providing secure and reliable services'
    },
    {
      icon: '💡',
      title: isKorean ? '간편함' : 'Simplicity',
      description: isKorean
        ? '복잡한 기술을 누구나 쉽게 사용할 수 있게 만듭니다'
        : 'Making complex technology accessible to everyone'
    }
  ];

  const milestones = [
    {
      year: '2020',
      event: isKorean ? 'Groove Inc. 설립' : 'Groove Inc. Founded',
      description: isKorean
        ? '글로벌 커뮤니케이션을 위한 새로운 시작'
        : 'A New Beginning for Global Communication'
    },
    {
      year: '2024',
      event: isKorean ? 'AI 실시간 화상통역 핵심기술' : 'Core Technology for AI Real-Time Video Translation',
      description: isKorean
        ? '30개 이상 언어 실시간 번역 지원'
        : 'Real-time translation for 30+ languages'
    },
    {
      year: '2025',
      event: isKorean ? '글로벌 비즈니스 데스크탑 앱 출시' : 'Global Business Desktop App Release',
      description: isKorean
        ? 'macOS & Windows 앱 새롭게 등장'
        : 'New native apps for macOS & Windows'
    }
  ];

  return (
    <div className="font-sans antialiased bg-gray-50">
      <Navigation t={t} currentLang={lang} onLanguageChange={handleLanguageChange} />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-primary-50 via-white to-secondary-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              {isKorean ? '우리의 이야기' : 'Our Story'}
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {isKorean
                ? 'AivueBiz는 AI 기술을 통해 전 세계 사람들이 언어 장벽 없이 소통할 수 있는 세상을 만들어갑니다.'
                : 'AivueBiz is creating a world where people can communicate without language barriers through AI technology.'}
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                {isKorean ? '우리의 미션' : 'Our Mission'}
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                {isKorean
                  ? '우리는 언어가 더 이상 비즈니스의 장벽이 되지 않는 세상을 꿈꿉니다. AI 기술을 통해 실시간 번역과 자연스러운 소통을 가능하게 하여, 전 세계 기업들이 더 효율적으로 협업할 수 있도록 돕습니다.'
                  : 'We dream of a world where language is no longer a barrier to business. Through AI technology, we enable real-time translation and natural communication, helping companies worldwide collaborate more efficiently.'}
              </p>
              <p className="text-lg text-gray-600">
                {isKorean
                  ? 'Groove Inc.는 서울에 본사를 둔 글로벌 스타트업으로, 혁신적인 기술과 사용자 중심의 디자인으로 비즈니스 커뮤니케이션의 패러다임을 바꾸고 있습니다.'
                  : 'Groove Inc., headquartered in Seoul, is a global startup changing the paradigm of business communication with innovative technology and user-centric design.'}
              </p>
            </div>
            <div className="relative">
              <div className="aspect-square bg-gradient-to-br from-primary-500 to-secondary-500 rounded-3xl shadow-2xl flex items-center justify-center">
                <svg className="w-32 h-32 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-12">
            {isKorean ? '우리의 가치' : 'Our Values'}
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
                <div className="text-5xl mb-4">{value.icon}</div>
                <h3 className="text-xl font-semibold mb-3 text-gray-900">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-12">
            {isKorean ? '우리 팀' : 'Our Team'}
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {teamMembers.map((member, index) => (
              <div key={index} className="text-center">
                <div className="w-48 h-48 mx-auto mb-6 rounded-full overflow-hidden bg-gray-100">
                  <img src={member.image} alt={member.name} className="w-full h-full object-cover" />
                </div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-2">{member.name}</h3>
                <p className="text-primary-600 font-medium mb-3">{member.role}</p>
                <p className="text-gray-600">{member.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-12">
            {isKorean ? '우리의 여정' : 'Our Journey'}
          </h2>
          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-primary-200"></div>
            {milestones.map((milestone, index) => (
              <div key={index} className={`relative mb-12 ${index % 2 === 0 ? 'md:pr-1/2' : 'md:pl-1/2 md:text-right'}`}>
                <div className={`md:w-1/2 ${index % 2 === 0 ? 'md:pr-12' : 'md:ml-auto md:pl-12'}`}>
                  <div className="bg-white rounded-2xl p-8 shadow-lg">
                    <div className="text-primary-600 font-bold text-2xl mb-2">{milestone.year}</div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{milestone.event}</h3>
                    <p className="text-gray-600">{milestone.description}</p>
                  </div>
                </div>
                <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-primary-600 rounded-full"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary-600 to-secondary-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            {isKorean ? '함께 성장해요' : 'Grow With Us'}
          </h2>
          <p className="text-xl text-white/90 mb-8">
            {isKorean
              ? '글로벌 커뮤니케이션의 미래를 함께 만들어갈 인재를 찾고 있습니다.'
              : 'We are looking for talent to create the future of global communication together.'}
          </p>
          <a href="#" onClick={(e) => { e.preventDefault(); showToast(isKorean ? '🔨 준비 중입니다' : '🔨 Coming Soon'); }} className="inline-block px-8 py-4 bg-white text-primary-600 rounded-xl font-semibold hover:shadow-xl transition-all hover:scale-105 cursor-pointer">
            {isKorean ? '채용 정보 보기' : 'View Careers'}
          </a>
        </div>
      </section>

      <Footer t={t} />
    </div>
  );
};

export default AboutPage;